import { useState } from 'react';

import {
  Controller,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import TextareaAutosize from 'react-textarea-autosize';

import { FormFieldTooltipIndexProps } from '../../../types';
import { cn } from '../../../utils/classUtils';
import {
  MULTILINE_INPUT_MAX_ROWS,
  MULTILINE_INPUT_MIN_ROWS,
} from '../../../utils/LeoUtils';

export interface LeoControlledTextInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends UseControllerProps<TFieldValues, TName>,
    FormFieldTooltipIndexProps {
  noBorder?: boolean;
  placeholder?: string;
  hideErrorMessage?: boolean;
  startAdornment?: React.ReactElement;
  endAdornment?: React.ReactElement;
  isRequired?: boolean;
  helperText?: string;
  maxLength?: number;
  disabled?: boolean;
  onSubmit?(): void;
  rightIcon?: React.ReactElement;
}

const LeoControlledTextInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  noBorder,
  placeholder,
  hideErrorMessage = false,
  tooltipIndex,
  shouldUnregister = true,
  helperText,
  isRequired = false,
  maxLength,
  disabled = false,
  onSubmit,
  rightIcon,
  ...rest
}: LeoControlledTextInputProps<TFieldValues, TName>) => {
  const [isActive, setIsActive] = useState(false);
  return (
    <Controller
      shouldUnregister={shouldUnregister}
      {...rest}
      render={({ field: { onChange, value, name, onBlur, ref } }) => (
        <div
          className={cn(
            'relative flex items-stretch rounded-3xl overflow-hidden ',
            value ? 'text-white' : 'text-reskin-regent-900',
            {
              'bg-gradient-to-r from-reskin-aqua via-reskin-neoleo-purple to-reskin-legacy-blue w-full':
                isActive,
              'bg-white bg-opacity-10': !isActive,
            },
          )}
        >
          <div
            className={cn(
              'flex items-center w-full bg-reskin-regent-900 rounded-3xl m-px ',
              {
                'bg-reskin-primary-navy': isActive,
              },
            )}
          >
            <TextareaAutosize
              id={name}
              ref={ref}
              value={value}
              onChange={onChange}
              onBlur={(e) => {
                onChange(e.target.value?.trim());
                setIsActive(false);
                onBlur();
              }}
              onFocus={() => setIsActive(true)}
              inputMode='text'
              name={name}
              placeholder={placeholder}
              className={cn(
                'appearance-none bg-transparent w-full resize-none rounded-3xl border-0 focus:ring-0 focus:outline-none font-normal text-base font-inter italic p-1.5',
                {
                  'not-italic': value,
                },
              )}
              wrap='soft'
              minRows={MULTILINE_INPUT_MIN_ROWS}
              maxRows={MULTILINE_INPUT_MAX_ROWS}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && onSubmit) {
                  e.preventDefault();
                  onSubmit();
                }
              }}
              maxLength={maxLength}
              data-tooltip-index={tooltipIndex}
              disabled={disabled}
            />
            <div className='px-3'>{rightIcon}</div>
          </div>
        </div>
      )}
    />
  );
};
export default LeoControlledTextInput;
